import { ProblemStates } from "../constants/feedback";
import { ResourceStrings } from "../ResourceStrings";
import { FeedbackStateConfigMap, ProblemStateConfig } from "./feedbackStateConfig";
import { instance } from "./serverConfig";

export const problemStates: FeedbackStateConfigMap<ProblemStateConfig> = {
    [ProblemStates.duplicate]: {
        categoryLabel: "Closed",
        subLabel: "Duplicate",
        label: ResourceStrings.stateDuplicate(),
        description: "Closed - Duplicate indicates that your issue has already been reported by another person. We'll provide you a link to the existing problem report.",
        backgroundColor: "#605E5C",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: true,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.fixed]: {
        categoryLabel: "Fixed",
        label: ResourceStrings.stateFixed(),
        description: instance.isInternal ? "Closed - Fixed indicates we have released a fix for the problem. You can let us know whether the solution worked for you on the ticket page under 'Official Solution'." :
            "Closed - Fixed indicates that we've released a fix for the problem. The problem is also now tagged with a 'fixed in:' tag specifying the release version.",
        backgroundColor: "#197B1B",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        isFixedState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.lowerPriority]: {
        categoryLabel: "Closed",
        label: ResourceStrings.stateClosedLowPri(),
        description: "Closed - Lower Priority To focus on bringing each of you in our developer community the best value, we prioritize issues with the highest customer impact. Although we're unable to address this particular issue at this time, be assured that all your feedback is valuable and helps improve Visual Studio.",
        backgroundColor: "#605E5C",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.notEnoughInfo]: {
        categoryLabel: "Closed",
        subLabel: "Not enough information",
        label: ResourceStrings.stateNotEnoughInfo(),
        description: instance.isInternal ? "Closed - Not Enough Info indicates that we do not have enough information to investigate your problem. We will automatically move a problem report to this state 7 days after marking it Need More Info if we have not received additional information. We can reconsider your problem report after the necessary information is provided." :
            "Closed - Not Enough Info indicates that we don't have enough information to investigate this for you. We'll be happy to reconsider the feedback after the necessary information is available.",
        backgroundColor: "#605E5C",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.notABug]: {
        categoryLabel: "Closed",
        subLabel: "Not a bug",
        label: ResourceStrings.stateNotBug(),
        description: "Closed - Not a Bug indicates that we've determined that the reported functionality is by current design.",
        backgroundColor: "#605E5C",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.otherProduct]: {
        categoryLabel: "Closed",
        subLabel: "Other product",
        label: ResourceStrings.stateClosedOtherProduct(),
        description: instance.isInternal ? "Closed - Other Product indicates that your issue applies to another internal engineering system tool." :
            "Closed - Other Product indicates we've determined that your issue applies to another product. See the comment from Microsoft for which external product and any related links.",
        backgroundColor: "#605E5C",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.pending]: {
        categoryLabel: "Fixed",
        subLabel: "Pending release",
        label: ResourceStrings.statePendingRelease(),
        description: "Fixed - Pending Release indicates that we have a fix for the problem and it will be available in an upcoming preview or release. When the fix becomes available in a preview, the problem is tagged with a 'fixed in' tag specifying the preview version.",
        backgroundColor: "#197B1B",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: false,
        isFixedState: true,
        votesLocked: false,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: false,
        solutionsLocked: false,
        requiresSolution: false,
        requiresComment: true,
    },
    [ProblemStates.moreInfo]: {
        categoryLabel: "Need More Info",
        label: ResourceStrings.stateMoreInfo(),
        description: instance.isInternal ? "Need More Info indicates we need more diagnostic information from you in order to move forward with the investigation. Check the ticket comments, where we will specify the additional information needed." :
            "Need More Info indicates that we need more diagnostic information from you so that we can go forward with the investigation.",
        backgroundColor: "#D63C18",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: false,
        votesLocked: false,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: false,
        solutionsLocked: false,
        requiresSolution: false,
        requiresComment: true,
    },
    [ProblemStates.new]: {
        categoryLabel: "New",
        label: ResourceStrings.stateNew(),
        description: "New indicates that the bug or issue is newly reported, and no action has been taken on it yet.",
        backgroundColor: "#106EBE",
        textColor: "#FFFFFF",
        isInitialState: true,
        isFinalState: false,
        votesLocked: false,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: false,
        solutionsLocked: false,
        requiresSolution: false,
        requiresComment: false,
    },
    [ProblemStates.triaged]: {
        categoryLabel: "Active",
        label: ResourceStrings.stateTriaged(),
        description: instance.isInternal ? "Active indicates we have launched work to address your problem." :
            "Triaged indicates that preliminary steps such as moderation, translation, and initial check for duplicates are complete. Your ticket has been routed to the appropriate engineering team for consideration.",
        backgroundColor: "#106EBE",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: false,
        votesLocked: false,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: false,
        solutionsLocked: false,
        requiresSolution: false,
        requiresComment: true,
    },
    [ProblemStates.consideration]: {
        categoryLabel: "Active",
        subLabel: "",
        label: ResourceStrings.stateUnderConsideration(),
        description: "Under Consideration indicates that Microsoft is reviewing your problem for community impact and will prioritize it accordingly. If the community impact isn't clear or significant yet, we'll continue to monitor the problem in this state.",
        backgroundColor: "#106EBE",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: false,
        votesLocked: false,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: false,
        solutionsLocked: false,
        requiresSolution: false,
        requiresComment: true,
    },
    [ProblemStates.investigation]: {
        categoryLabel: "Under Investigation",
        label: ResourceStrings.stateInvestigating(),
        description: "Under Investigation indicates that engineers are actively investigating your problem to find a resolution.",
        backgroundColor: "#985A00",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: false,
        votesLocked: false,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: false,
        solutionsLocked: false,
        requiresSolution: false,
        requiresComment: true,
    },
    [ProblemStates.invalid]: {
        categoryLabel: "Invalid State",
        label: "Invalid State",
        description: "The state of this feedback is invalid. Please log a bug with the VS Feedback team.",
        backgroundColor: "#000000",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    // OXO specific states
    [ProblemStates.active]: {
        categoryLabel: "Active",
        label: "Active",
        description: instance.isInternal ? "Active indicates we have launched work to address your problem." :
            "Active state indicate problem is under consideration and final decision is pending.",
        backgroundColor: "#106EBE",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: false,
        votesLocked: false,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: false,
        solutionsLocked: false,
        requiresSolution: false,
        requiresComment: true,
    },
    [ProblemStates.closedByDesign]: {
        categoryLabel: "Closed By Design",
        label: "Closed By Design",
        description: instance.isInternal ? "Closed - By Design indicates that the system is working as intended, and the problem you are reporting is within the designed behavior." :
            "This state indicate behavior posted is by design.",
        backgroundColor: "#197B1B",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.closedNotRepro]: {
        categoryLabel: "Closed Not Repro",
        label: "Closed Not Repro",
        description: instance.isInternal ? "Closed - Not Repro indicates we are unable to reproduce the problem you encountered." :
            "This state indicate engineering team was not able to repro the problem reported in post.",
        backgroundColor: "#197B1B",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.closedObsolete]: {
        categoryLabel: "Closed Obsolete",
        label: "Closed Obsolete",
        description: instance.isInternal ? "Closed - Obsolete indicates that your report is about a problem with a system or process that is no longer exists or is no longer supported." :
            "This state indicate reported problem is obsolete.",
        backgroundColor: "#197B1B",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.wontFix]: {
        categoryLabel: "Won't Fix",
        label: "Won't Fix",
        description: instance.isInternal ? "Will Not Fix indicates that we are unable to fix the problem you reported. This can be for several reasons, including limited resourcing, low priority, and clashes with planned changes to the tool." :
            "This state indicate that engineering team will not be fixing issue which is deemed lower priority.",
        backgroundColor: "#197B1B",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
    [ProblemStates.willNotFix]: {
        categoryLabel: "Will Not Fix",
        label: "Will Not Fix",
        description: "Will Not Fix indicates that we are unable to fix the problem you reported. This can be for several reasons, including limited resourcing, low priority, and clashes with planned changes to the tool.",
        backgroundColor: "#197B1B",
        textColor: "#FFFFFF",
        isInitialState: false,
        isFinalState: true,
        votesLocked: true,
        votesLockedOnSolutions: false,
        reactionsLocked: false,
        commentsLocked: true,
        solutionsLocked: true,
        requiresSolution: true,
        requiresComment: false,
    },
};